import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { fmt } from '../lib/helpers';
import { invokeAuth0EdgeFunction } from '../lib/edgeFunctions';
import Badge from '../components/Badge';
import Loader from '../components/Loader';
import Modal from '../components/Modal';

const PLAN_TIERS = ['trial', 'starter', 'pro', 'enterprise'];
const SUBSCRIPTION_STATUSES = ['trialing', 'active', 'past_due', 'cancelled'];

function toDateInputValue(iso) {
  if (!iso) return '';
  return String(iso).slice(0, 10);
}

// ── Confirm-by-typing modals ────────────────────────────────────────────────
function DeleteUserModal({ user, onConfirm, onCancel, busy, error }) {
  const [confirmEmail, setConfirmEmail] = useState('');
  const matches = confirmEmail.trim().toLowerCase() === (user.email || '').trim().toLowerCase();

  return (
    <Modal title="Delete user permanently" onClose={onCancel} busy={busy} footer={
      <>
        <button type="button" className="btn-ghost" onClick={onCancel} disabled={busy}>Cancel</button>
        <button
          type="button"
          className="btn-danger"
          disabled={!matches || busy}
          onClick={() => onConfirm(confirmEmail)}
        >
          {busy ? 'Deleting…' : 'Delete permanently'}
        </button>
      </>
    }>
      <p>
        This permanently deletes <strong>{user.full_name || user.email}</strong> and cascades to
        their time logs, role assignments, resource allocations, charter approvals, and other
        linked records. This cannot be undone — consider <strong>Deactivate</strong> instead if you
        just need to revoke access.
      </p>
      <p style={{ marginTop: 12 }}>
        Type <strong>{user.email}</strong> to confirm:
      </p>
      <input
        className="form-input"
        value={confirmEmail}
        onChange={(e) => setConfirmEmail(e.target.value)}
        placeholder={user.email}
        autoFocus
      />
      {error && <div className="form-error" style={{ marginTop: 8 }}>{error}</div>}
    </Modal>
  );
}

function PurgeCompanyModal({ organisation, onConfirm, onCancel, busy, error }) {
  const [confirmName, setConfirmName] = useState('');
  const matches = confirmName === organisation.name;

  return (
    <Modal title="Permanently delete this company" onClose={onCancel} busy={busy} footer={
      <>
        <button type="button" className="btn-ghost" onClick={onCancel} disabled={busy}>Cancel</button>
        <button
          type="button"
          className="btn-danger"
          disabled={!matches || busy}
          onClick={() => onConfirm(confirmName)}
        >
          {busy ? 'Deleting…' : 'Delete company permanently'}
        </button>
      </>
    }>
      <p>
        This permanently deletes <strong>{organisation.name}</strong> and every record that
        belongs to it — programmes, projects, users, and everything underneath them (~25 tables).
        This cannot be undone. Consider <strong>Suspend</strong> instead if you just need to block
        their access while keeping the data.
      </p>
      <p style={{ marginTop: 12 }}>
        Type <strong>{organisation.name}</strong> to confirm:
      </p>
      <input
        className="form-input"
        value={confirmName}
        onChange={(e) => setConfirmName(e.target.value)}
        placeholder={organisation.name}
        autoFocus
      />
      {error && <div className="form-error" style={{ marginTop: 8 }}>{error}</div>}
    </Modal>
  );
}

export default function PlatformCompanyDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { getAccessTokenSilently } = useAuth0();

  const [organisation, setOrganisation] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);

  const [subForm, setSubForm] = useState({ plan_tier: 'trial', subscription_status: 'trialing', trial_ends_at: '' });
  const [subSaving, setSubSaving] = useState(false);
  const [suspendBusy, setSuspendBusy] = useState(false);
  const [suspendReason, setSuspendReason] = useState('');

  const [userBusyId, setUserBusyId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteError, setDeleteError] = useState(null);
  const [purgeOpen, setPurgeOpen] = useState(false);
  const [purgeError, setPurgeError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await invokeAuth0EdgeFunction(
        'platform-console',
        { action: 'company_detail', organisation_id: id },
        getAccessTokenSilently
      );
      setOrganisation(data.organisation);
      setUsers(Array.isArray(data.users) ? data.users : []);
      setSubForm({
        plan_tier: data.organisation.plan_tier || 'trial',
        subscription_status: data.organisation.subscription_status || 'trialing',
        trial_ends_at: toDateInputValue(data.organisation.trial_ends_at),
      });
    } catch (e) {
      setError(e?.message || 'Failed to load company');
    } finally {
      setLoading(false);
    }
  }, [id, getAccessTokenSilently]);

  useEffect(() => { load(); }, [load]);

  function showToast(msg, isError = false) {
    setToast({ msg, isError });
    setTimeout(() => setToast(null), 3500);
  }

  async function saveSubscription() {
    setSubSaving(true);
    try {
      await invokeAuth0EdgeFunction('platform-console', {
        action: 'update_subscription',
        organisation_id: id,
        plan_tier: subForm.plan_tier,
        subscription_status: subForm.subscription_status,
        trial_ends_at: subForm.trial_ends_at ? new Date(subForm.trial_ends_at).toISOString() : null,
      }, getAccessTokenSilently);
      showToast('Subscription updated');
      load();
    } catch (e) {
      showToast(e?.message || 'Update failed', true);
    } finally {
      setSubSaving(false);
    }
  }

  async function toggleSuspend(suspend) {
    setSuspendBusy(true);
    try {
      await invokeAuth0EdgeFunction('platform-console', {
        action: 'suspend_company',
        organisation_id: id,
        suspended: suspend,
        reason: suspendReason,
      }, getAccessTokenSilently);
      showToast(suspend ? 'Company suspended' : 'Company unsuspended');
      setSuspendReason('');
      load();
    } catch (e) {
      showToast(e?.message || 'Update failed', true);
    } finally {
      setSuspendBusy(false);
    }
  }

  async function handlePurge(confirmName) {
    setPurgeError(null);
    try {
      const data = await invokeAuth0EdgeFunction('platform-console', {
        action: 'purge_company',
        organisation_id: id,
        confirm_name: confirmName,
      }, getAccessTokenSilently);
      setPurgeOpen(false);
      navigate('/platform', { replace: true });
      showToast(data?.message || 'Company deleted');
    } catch (e) {
      setPurgeError(e?.message || 'Delete failed');
    }
  }

  async function handleResetPassword(user) {
    setUserBusyId(user.id);
    try {
      const data = await invokeAuth0EdgeFunction('platform-console', {
        action: 'reset_user_password',
        profile_id: user.id,
      }, getAccessTokenSilently);
      showToast(data?.message || `Password reset email sent to ${user.email}.`);
    } catch (e) {
      showToast(e?.message || 'Reset failed', true);
    } finally {
      setUserBusyId(null);
    }
  }

  async function handleToggleDeactivate(user) {
    setUserBusyId(user.id);
    try {
      const deactivate = !user.is_deactivated;
      await invokeAuth0EdgeFunction('platform-console', {
        action: 'deactivate_user',
        profile_id: user.id,
        deactivated: deactivate,
      }, getAccessTokenSilently);
      showToast(deactivate ? `${user.full_name || user.email} deactivated` : `${user.full_name || user.email} reactivated`);
      load();
    } catch (e) {
      showToast(e?.message || 'Update failed', true);
    } finally {
      setUserBusyId(null);
    }
  }

  async function handleDeleteUser(confirmEmail) {
    setDeleteError(null);
    try {
      const data = await invokeAuth0EdgeFunction('platform-console', {
        action: 'delete_user',
        profile_id: deleteTarget.id,
        confirm_email: confirmEmail,
      }, getAccessTokenSilently);
      setDeleteTarget(null);
      showToast(data?.message || 'User deleted');
      load();
    } catch (e) {
      setDeleteError(e?.message || 'Delete failed');
    }
  }

  if (loading) return <Loader />;
  if (error || !organisation) {
    return (
      <div className="content">
        <div className="form-error">{error || 'Company not found'}</div>
        <button type="button" className="btn-ghost" style={{ marginTop: 12 }} onClick={() => navigate('/platform')}>
          Back to companies
        </button>
      </div>
    );
  }

  return (
    <>
      <div className="topbar">
        <div>
          <div className="tbtitle">{organisation.name}</div>
          <div className="tbcrumb">
            Created {fmt(organisation.created_at)}
            {organisation.is_suspended && <Badge variant="">Suspended</Badge>}
          </div>
        </div>
        <button type="button" className="btn-ghost" onClick={() => navigate('/platform')}>Back to companies</button>
      </div>

      <div className="content">
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="chdr"><span className="ctitle">Subscription</span></div>
          <div className="form-2col" style={{ padding: '0 20px 20px' }}>
            <div className="form-row">
              <label className="form-label">Plan tier</label>
              <select
                className="form-select"
                value={subForm.plan_tier}
                onChange={(e) => setSubForm((p) => ({ ...p, plan_tier: e.target.value }))}
              >
                {PLAN_TIERS.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div className="form-row">
              <label className="form-label">Status</label>
              <select
                className="form-select"
                value={subForm.subscription_status}
                onChange={(e) => setSubForm((p) => ({ ...p, subscription_status: e.target.value }))}
              >
                {SUBSCRIPTION_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div className="form-row">
              <label className="form-label">Trial ends</label>
              <input
                type="date"
                className="form-input"
                value={subForm.trial_ends_at}
                onChange={(e) => setSubForm((p) => ({ ...p, trial_ends_at: e.target.value }))}
              />
              <span style={{ fontSize: 10, color: '#888780' }}>Push this out to extend their free tier</span>
            </div>
          </div>
          <div style={{ padding: '0 20px 20px' }}>
            <button type="button" className="btn-primary" disabled={subSaving} onClick={saveSubscription}>
              {subSaving ? 'Saving…' : 'Save subscription'}
            </button>
          </div>
        </div>

        <div className="card" style={{ marginBottom: 16 }}>
          <div className="chdr"><span className="ctitle">Company controls</span></div>
          <div style={{ padding: '0 20px 20px' }}>
            {organisation.is_suspended ? (
              <>
                <p style={{ color: '#5f5e5a', marginBottom: 8 }}>
                  Suspended{organisation.suspended_reason ? `: ${organisation.suspended_reason}` : ''}. All logins for this company are blocked.
                </p>
                <button type="button" className="btn-primary" disabled={suspendBusy} onClick={() => toggleSuspend(false)}>
                  {suspendBusy ? 'Working…' : 'Unsuspend'}
                </button>
              </>
            ) : (
              <>
                <input
                  className="form-input"
                  style={{ marginBottom: 8, maxWidth: 420 }}
                  placeholder="Reason (optional)"
                  value={suspendReason}
                  onChange={(e) => setSuspendReason(e.target.value)}
                />
                <div>
                  <button type="button" className="btn-ghost" disabled={suspendBusy} onClick={() => toggleSuspend(true)}>
                    {suspendBusy ? 'Working…' : 'Suspend company'}
                  </button>
                </div>
              </>
            )}

            <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid #e8e6dd' }}>
              <div style={{ fontWeight: 600, color: '#E24B4A', marginBottom: 6 }}>Danger zone</div>
              <p style={{ color: '#5f5e5a', marginBottom: 8 }}>
                Permanently delete this company and everything in it. Not reversible.
              </p>
              <button type="button" className="btn-danger" onClick={() => setPurgeOpen(true)}>
                Purge company…
              </button>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="chdr">
            <span className="ctitle">Users</span>
            <Badge variant="pu">{users.length}</Badge>
          </div>

          {users.length === 0 ? (
            <div className="empty">No users in this company.</div>
          ) : (
            <div>
              <div className="ua-header-row">
                <span style={{ flex: 1.4 }}>Name</span>
                <span style={{ flex: 1.6 }}>Email</span>
                <span style={{ flex: 0.8 }}>Role</span>
                <span style={{ flex: 0.9 }}>Status</span>
                <span style={{ flex: 0.8 }}>Joined</span>
                <span style={{ flex: '0 0 280px' }} />
              </div>
              {users.map((u) => (
                <div key={u.id} className="ua-row">
                  <div style={{ flex: 1.4, minWidth: 0 }}>
                    <div className="ua-name">{u.full_name || '—'}</div>
                  </div>
                  <div style={{ flex: 1.6, minWidth: 0 }}>
                    <div className="ua-email">{u.email || '—'}</div>
                  </div>
                  <div style={{ flex: 0.8 }}>
                    <span className="ua-meta">{u.platform_role}</span>
                  </div>
                  <div style={{ flex: 0.9 }}>
                    {u.is_deactivated ? (
                      <Badge variant="">Deactivated</Badge>
                    ) : u.is_active ? (
                      <Badge variant="bl">Active</Badge>
                    ) : (
                      <Badge variant="pu">Pending</Badge>
                    )}
                  </div>
                  <div style={{ flex: 0.8 }}>
                    <span className="ua-meta">{fmt(u.created_at)}</span>
                  </div>
                  <div style={{ flex: '0 0 280px', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                    <button
                      type="button"
                      className="row-act"
                      disabled={!u.is_active || userBusyId === u.id}
                      onClick={() => handleResetPassword(u)}
                      title={u.is_active ? 'Send password reset email' : 'User has not signed in yet'}
                    >
                      Reset password
                    </button>
                    <button
                      type="button"
                      className="row-act"
                      disabled={userBusyId === u.id}
                      onClick={() => handleToggleDeactivate(u)}
                    >
                      {u.is_deactivated ? 'Reactivate' : 'Deactivate'}
                    </button>
                    <button
                      type="button"
                      className="row-act row-act-danger"
                      disabled={userBusyId === u.id}
                      onClick={() => { setDeleteError(null); setDeleteTarget(u); }}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {deleteTarget && (
        <DeleteUserModal
          user={deleteTarget}
          onConfirm={handleDeleteUser}
          onCancel={() => setDeleteTarget(null)}
          busy={userBusyId === deleteTarget.id}
          error={deleteError}
        />
      )}

      {purgeOpen && (
        <PurgeCompanyModal
          organisation={organisation}
          onConfirm={handlePurge}
          onCancel={() => setPurgeOpen(false)}
          busy={false}
          error={purgeError}
        />
      )}

      {toast && (
        <div className={toast.isError ? 'toast-error' : 'toast-success'}>
          {toast.msg}
        </div>
      )}
    </>
  );
}
